// src/lib/jsonld.js
import { destByName, destForRoute } from './route-resolvers.js';
// Schema.org JSON-LD builders for all entity page types.
// Pure functions: no I/O, no fetch. Context shape:
//   { path, siteUrl, settings, allData, entity, entityType, faqs, description }

import { CHARTER_SERVICES_FALLBACK } from './charter-services-fallback.js';
import { routeFromPriceMin, routesFromPriceMin } from './fleet-card-price.js';
import { BRAND } from '../lib/brand-config.js';

const SCHEMA = 'https://schema.org';

function clean(obj) {
  if (obj === null || obj === undefined || obj === '') return undefined;
  if (Array.isArray(obj)) {
    const out = obj.map(clean).filter(x => x !== undefined);
    return out.length ? out : undefined;
  }
  if (typeof obj !== 'object') return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    const c = clean(v);
    if (c !== undefined) out[k] = c;
  }
  return Object.keys(out).length ? out : undefined;
}

function absoluteUrl(url, siteUrl) {
  if (!url) return undefined;
  if (typeof url !== 'string') return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return siteUrl + (url.startsWith('/') ? url : '/' + url);
}

function destinationSlug(d) {
  return d?.slug || (d?.name ? d.name.toLowerCase().replace(/\s+/g, '-') : null);
}

function countryToISO(country) {
  if (!country) return 'GR';
  const map = { Greece: 'GR', Cyprus: 'CY', Malta: 'MT', Italy: 'IT', Germany: 'DE', 'United Kingdom': 'GB', France: 'FR', Spain: 'ES', Netherlands: 'NL', Switzerland: 'CH', Austria: 'AT', 'United States': 'US', 'United Arab Emirates': 'AE' };
  return map[country] || country;
}

// Published prices come EXCLUSIVELY from the precomputed route.cardFromPrice
// map (the same canonical customer-total minimum every visible price surface
// uses — see fleet-card-price.js). The former brokerToCustomer recompute over
// route.pricing[] could disagree with the visible "from EUR X"/"Price on
// request" labels; JSON-LD offers must never publish a price the page itself
// does not show. Number.isFinite-guarded: no finite value → NO offers block.
function minRoutePrice(route) {
  return routeFromPriceMin(route?.cardFromPrice);
}

// THE offer set — one extraction for count and highPrice (GSC warning
// 'highPrice fehlt', 2026-09-05). Filter identical to routeFromPriceMin, so
// lowPrice (min), offerCount (length) and highPrice (max) always describe the
// SAME options; with offerCount 1, high === low by construction.
function routePriceValues(route) {
  return Object.values(route?.cardFromPrice || {})
    .filter(v => typeof v === 'number' && Number.isFinite(v) && v > 0);
}

function countRouteOffers(route) {
  return routePriceValues(route).length;
}

function maxRoutePrice(route) {
  const vals = routePriceValues(route);
  return vals.length ? Math.max(...vals) : null;
}

function minAircraftPrice(aircraftId, routes) {
  const prices = [];
  for (const r of routes || []) {
    const v = r?.cardFromPrice?.[aircraftId];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) prices.push(v);
  }
  return prices.length ? Math.min(...prices) : null;
}

function countAircraftOffers(aircraftId, routes) {
  let n = 0;
  for (const r of routes || []) {
    const v = r?.cardFromPrice?.[aircraftId];
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) n++;
  }
  return n;
}

// AggregateRating — PREPARED, INACTIVE path. Renders ONLY when genuine review
// data exists in the CMS (an array of {rating} values 1..5, e.g. a future
// entity.reviews field). Today no entity carries reviews, so this always
// returns undefined and NO rating markup is emitted anywhere — fabricated
// ratings are a Google penalty risk and are never generated from thin air.
export function buildAggregateRating(reviews) {
  if (!Array.isArray(reviews)) return undefined;
  const vals = reviews
    .map(r => (typeof r === 'number' ? r : r?.rating))
    .filter(v => typeof v === 'number' && Number.isFinite(v) && v >= 1 && v <= 5);
  if (!vals.length) return undefined;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return {
    '@type': 'AggregateRating',
    ratingValue: Math.round(avg * 10) / 10,
    reviewCount: vals.length,
    bestRating: 5,
    worstRating: 1,
  };
}

// Review-snippet nodes (GSC fix 2026-09): Google supports review/aggregateRating
// only on specific types — Product among them; TouristTrip and Service are NOT
// on the list (Search Console: "Ungültiger Objekttyp für Feld <parent_node>").
// The route trip node and the destination service node therefore carry Product
// as an ADDITIONAL type (multi-type array); @id, name and offers stay put.
//
// NAME RULE (hard): review.author is the value truncated AT SAVE TIME in the
// CMS — it is NEVER joined from a booking and NEVER re-derived here. This
// module has no access to booking passenger data by construction.
//
// The list filter is IDENTICAL to buildAggregateRating's (finite rating 1..5),
// so aggregateRating.reviewCount always equals the review array length.
// A review without text stays in the list — reviewBody is simply omitted.
function buildReviewList(reviews) {
  if (!Array.isArray(reviews)) return undefined;
  const list = reviews
    .filter(r => r && typeof r?.rating === 'number' && Number.isFinite(r.rating) && r.rating >= 1 && r.rating <= 5)
    .map(r => clean({
      '@type': 'Review',
      author: r.author ? { '@type': 'Person', name: r.author } : undefined,
      datePublished: r.date || undefined,
      reviewBody: r.text || undefined,
      reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
    }));
  return list.length ? list : undefined;
}

// Product image fallback — the SAME source the visible pages use (App.jsx:
// entity image first, else the seoContent card/hero for this page). Absolute
// URL or undefined; callers omit the key when nothing resolves.
// ── Own-image chain for route/dest JSON-LD (owner rule 2026-09-03) ─────────
// The route hero renders TWO images (departure left, arrival right), both from
// the DESTINATION documents' own Sanity uploads — client-rendered, so they are
// invisible to a plain source check. The JSON-LD image now follows the SAME
// place resolution (destByName for the origin, destForRoute for the target —
// the exact functions the hero pipeline uses), so markup and display cannot
// name different places. Pexels-filled seoImages remain the LAST fallback only.
//
// Per place, the LARGER of the two uploads (image vs heroImage, pixel area
// parsed from the Sanity asset filename) wins — both show the same place, and
// Google cares about resolution; unparseable dims prefer heroImage.
function sanityImageDims(url) {
  const m = /-(\d+)x(\d+)\.[a-z]+(?:$|\?)/i.exec(String(url || ''));
  return m ? { w: Number(m[1]), h: Number(m[2]) } : null;
}
function largerPlaceImage(doc) {
  const i = doc?.image, h = doc?.heroImage;
  if (i && h) {
    const di = sanityImageDims(i), dh = sanityImageDims(h);
    if (di && dh) return (dh.w * dh.h >= di.w * di.h) ? h : i;
    return h; // dims unparseable → prefer heroImage
  }
  return h || i || null;
}
// 1200px recommendation via CDN params — the SAME trio og:image already uses
// (w=1200, q=80, fm=jpg; the CDN never upscales past the source). Only for
// clean cdn.sanity.io URLs; anything else passes through untouched.
function jsonLdImageUrl(url, siteUrl) {
  const abs = absoluteUrl(url, siteUrl);
  if (!abs) return null;
  if (abs.includes('cdn.sanity.io') && !abs.includes('?')) return abs + '?w=1200&q=80&fm=jpg';
  return abs;
}
// Route image array in TRAVEL ORDER: departure place first, then arrival.
// A place without an upload is skipped; an empty array → caller falls back.
function routePlaceImages(route, allData, siteUrl) {
  const dests = allData?.destinations || [];
  const places = [destByName(route.from, dests), destForRoute(route, dests)];
  const seen = new Set();
  const out = [];
  for (const p of places) {
    const u = jsonLdImageUrl(largerPlaceImage(p), siteUrl);
    if (u && !seen.has(u)) { seen.add(u); out.push(u); }
  }
  return out;
}

function seoImageFor(allData, pageType, slug, siteUrl) {
  const si = (allData?.seoImages || []).find(x => x && x.pageType === pageType && x.slug === slug);
  return absoluteUrl(si?.heroImage || si?.cardImage, siteUrl);
}

// Published reviews for one entity — the SINGLE matching rule for the
// on-page "What our clients say" sections AND the schema rating (2026-07-08):
// review.routeSlug === route.slug, review.destName === destination.name.
// Only CMS docs with published == true ever reach this list (the GROQ filters
// server-side); an empty list keeps every rating slot inactive.
export function publishedReviewsFor(entityType, entity, reviews) {
  if (!entity || !Array.isArray(reviews)) return [];
  if (entityType === 'route') return reviews.filter(r => r && r.routeSlug && r.routeSlug === entity.slug);
  if (entityType === 'destination' || entityType === 'dest') return reviews.filter(r => r && r.destName && r.destName === entity.name);
  return [];
}

// Readable journey label for one review — "Santorini → Mykonos".
//
// The review carries only routeSlug (the site query projects
// route->slug.current); the human-readable ends live on the route document,
// which is already in the payload with from/to/slug. So this resolves rather
// than denormalising: it costs ZERO extra bytes per review, unlike the
// helicopter type, which has no such counterpart on the wire.
//
// Falls back to destName for destination-only reviews, and returns null when
// neither resolves — callers then omit the line instead of printing a slug.
export function reviewRouteLabel(review, routes) {
  if (!review) return null;
  if (review.routeSlug) {
    const r = (routes || []).find(x => x && x.slug === review.routeSlug);
    if (r && r.from && r.to) return r.from + ' → ' + r.to;
  }
  return review.destName || null;
}

// THE shared list for the homepage section: every published review, newest
// first, each decorated with its readable routeLabel.
//
// Both the SSR block in index.astro and the React <ReviewsSection> call this
// with the same data, which is what makes "SSR and React show the same thing"
// a property of the code rather than a promise in a comment. The site query
// already filters published == true and orders by date desc; the sort here is
// a defensive re-assert for documents without a date (they sort last).
export function homepageReviews(reviews, routes) {
  if (!Array.isArray(reviews)) return [];
  return reviews
    .filter(Boolean)
    .map(r => ({ ...r, routeLabel: reviewRouteLabel(r, routes) }))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
}

// -- Entity builders --

export function buildOrganization(s, siteUrl) {
  const social = s?.socialLinks || {};
  const sameAs = [social.instagram, social.facebook, social.linkedin].filter(Boolean);
  const addr = s?.address;
  const hasAddr = addr && (addr.street || addr.city || addr.country);
  return clean({
    '@type': 'Organization',
    '@id': siteUrl + '/#org',
    name: s?.companyName || s?.siteName || BRAND.name,
    alternateName: (s?.companyName && s?.siteName && s.companyName !== s.siteName) ? s.siteName : undefined,
    url: siteUrl,
    logo: absoluteUrl(s?.logoUrl, siteUrl),
    email: s?.email,
    telephone: s?.phone,
    address: hasAddr ? {
      '@type': 'PostalAddress',
      streetAddress: addr.street,
      addressLocality: addr.city,
      postalCode: s?.zip,
      addressCountry: countryToISO(addr.country),
    } : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    contactPoint: s?.email ? {
      '@type': 'ContactPoint',
      email: s.email,
      telephone: s?.phone,
      contactType: 'customer service',
      availableLanguage: ['en'],
      areaServed: 'GR',
    } : undefined,
    // Prepared, INACTIVE (same contract as the entity nodes): renders only
    // from a future genuine settings.reviews array — never fabricated.
    aggregateRating: buildAggregateRating(s?.reviews),
    vatID: s?.companyRegNumber,
  });
}

// Fixed-price product entity (distribution preparation, 2026-07-08) — a
// Schema.org Product with a REAL fixed Offer, fed exclusively from a curated
// `product` CMS doc ("Athens–Santorini Helicopter Transfer", "Santorini
// Sightseeing Flight"). Renders ONLY when the doc is active and carries a
// positive finite fixedPrice — no doc / empty price → no node (today the CMS
// has zero product docs, so nothing renders anywhere).
export function buildFixedPriceProduct(product, siteUrl) {
  if (!product?.title) return undefined;
  const price = Number(product.fixedPrice);
  if (!Number.isFinite(price) || price <= 0) return undefined;
  if (product.active === false) return undefined;
  const url = product.route?.slug
    ? siteUrl + '/route/' + product.route.slug
    : siteUrl + (product.slug ? '/' + String(product.slug).replace(/^\//, '') : '');
  return clean({
    '@type': 'Product',
    '@id': url + '#product-' + (product.slug || product._id || 'p'),
    name: product.title,
    description: product.description,
    image: absoluteUrl(product.imageUrl, siteUrl),
    additionalProperty: [
      product.duration ? { '@type': 'PropertyValue', name: 'Duration', value: product.duration } : null,
      product.capacity ? { '@type': 'PropertyValue', name: 'Capacity', value: String(product.capacity) } : null,
    ].filter(Boolean),
    aggregateRating: buildAggregateRating(product.reviews),
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url,
    },
  });
}

export function buildWebSite(s, siteUrl) {
  return clean({
    '@type': 'WebSite',
    '@id': siteUrl + '/#website',
    url: siteUrl,
    name: s?.siteName || BRAND.name,
    publisher: { '@id': siteUrl + '/#org' },
    inLanguage: 'en',
  });
}

export function buildLocalBusiness(s, siteUrl, description) {
  const addr = s?.address;
  return clean({
    '@type': ['LocalBusiness', 'TravelAgency'],
    '@id': siteUrl + '/#business',
    name: s?.siteName || BRAND.name,
    description,
    url: siteUrl,
    email: s?.email,
    telephone: s?.phone,
    image: absoluteUrl(s?.logoUrl, siteUrl),
    priceRange: '\u20AC\u20AC\u20AC\u20AC',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Credit Card',
    address: {
      '@type': 'PostalAddress',
      streetAddress: addr?.street,
      addressLocality: addr?.city || 'Athens',
      postalCode: s?.zip,
      addressCountry: countryToISO(addr?.country) || 'GR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: (typeof s?.geoLat === 'number') ? s.geoLat : 37.893,
      longitude: (typeof s?.geoLng === 'number') ? s.geoLng : 23.726,
    },
  });
}

export function buildFAQPage(faqs) {
  if (!Array.isArray(faqs) || !faqs.length) return undefined;
  const entities = faqs.map(f => {
    const q = f?.q || f?.question;
    const a = f?.a || f?.answer;
    if (!q || !a) return null;
    return { '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } };
  }).filter(Boolean);
  if (!entities.length) return undefined;
  return { '@type': 'FAQPage', mainEntity: entities };
}

// ItemList for a category listing page (/fleet, /hotels, /destinations,
// /routes, /tours). Each element points at a detail-page URL so search engines
// see the full set of items the category links to. Read-only over allData.
export function buildCategoryItemList(category, allData, siteUrl) {
  let items = [];
  if (category === 'fleet') {
    items = (allData?.fleet || []).filter(f => f?.id && f.hideFromFleetPage !== true)
      .map(f => ({ name: f.name, url: '/fleet/' + f.id }));
  } else if (category === 'hotels') {
    items = (allData?.hotels || []).filter(h => h?.id)
      .map(h => ({ name: h.name, url: '/hotel/' + h.id }));
  } else if (category === 'destinations') {
    items = (allData?.destinations || []).filter(d => d && d.time !== 'Hub' && d.name)
      .map(d => ({ name: d.name, url: '/dest/' + destinationSlug(d) }));
  } else if (category === 'routes') {
    items = (allData?.routes || []).filter(r => r?.slug)
      .map(r => ({ name: `${r.from} → ${r.to}`, url: '/route/' + r.slug }));
  } else if (category === 'tours') {
    items = (allData?.tours || []).filter(t => t?.id)
      .map(t => ({ name: t.name, url: '/tour/' + t.id }));
  }
  if (!items.length) return undefined;
  return {
    '@type': 'ItemList',
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      url: absoluteUrl(it.url, siteUrl),
    })),
  };
}

// The Home crumb resolved to "https://www.heligreece.com/" — the only URL on
// the site carrying a trailing slash, while the homepage's own
// <link rel="canonical"> is "https://www.heligreece.com". Both forms are
// equivalent to Google; emitting one of each just makes the breadcrumb disagree
// with the canonical for no reason. Normalised here, at the one place every
// crumb passes through, so no call site has to remember it.
function crumbUrl(url, siteUrl) {
  if (url === '/' || url === '') return siteUrl;
  return absoluteUrl(url, siteUrl);
}

export function buildBreadcrumb(items, siteUrl) {
  if (!items?.length) return undefined;
  // Every ListItem MUST carry `item` (a URL) — Google rejects a BreadcrumbList
  // whose itemListElement lacks item (GSC "missing field 'item'"). A crumb for a
  // category with no landing page (e.g. "Charter services", which had no url) is
  // DROPPED rather than emitted item-less; positions are then assigned 1..N so the
  // sequence stays contiguous and every emitted ListItem has position+name+item.
  const withUrl = (items || []).filter(it => it && it.name && it.url);
  if (!withUrl.length) return undefined;
  return {
    '@type': 'BreadcrumbList',
    itemListElement: withUrl.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: crumbUrl(it.url, siteUrl),
    })),
  };
}

export function buildTouristTrip(route, allData, siteUrl) {
  if (!route?.from || !route?.to) return undefined;
  const slug = route.slug || `${route.from}-${route.to}`.toLowerCase().replace(/\s+/g, '-');
  const url = siteUrl + '/route/' + slug;
  const low = minRoutePrice(route);
  const count = countRouteOffers(route);
  const high = maxRoutePrice(route);
  // Product joins the type list so Google accepts review/aggregateRating here
  // (review-snippet rule); TouristTrip stays for trip semantics. @id unchanged.
  // Images: the OWN uploads of the involved places, travel order (the same
  // resolution the split hero uses); Pexels seoImages only as last fallback.
  const placeImgs = routePlaceImages(route, allData, siteUrl);
  const seoImg = placeImgs.length ? null : seoImageFor(allData, 'route', slug, siteUrl);
  const imgs = placeImgs.length ? placeImgs : (seoImg ? [seoImg] : null);
  return clean({
    '@type': ['Product', 'TouristTrip'],
    '@id': url + '#trip',
    name: `${route.from} to ${route.to} Helicopter Charter`,
    description: `Private helicopter flight from ${route.from} to ${route.to}${route.flightMin ? ' (' + route.flightMin + ' minutes)' : ''}.`,
    url,
    image: imgs || undefined,
    brand: { '@id': siteUrl + '/#org' },
    touristType: ['Leisure travellers', 'Business travellers'],
    itinerary: {
      '@type': 'ItemList',
      itemListElement: [
        { '@type': 'Place', name: route.from },
        { '@type': 'Place', name: route.to },
      ],
    },
    offers: (low != null) ? {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: low,
      ...(high != null ? { highPrice: high } : {}),
      offerCount: count || 1,
      availability: 'https://schema.org/InStock',
      url,
    } : undefined,
    review: buildReviewList(route.reviews),
    aggregateRating: buildAggregateRating(route.reviews),
    provider: { '@id': siteUrl + '/#org' },
  });
}

// Service node for a DESTINATION page — the bookable charter service to that
// destination, with provider + a finite-guarded AggregateOffer. The price is
// the min over every Athens→destination route cardFromPrice (the SAME source
// the visible dest cards/Key Facts use via getDestFromAthensMin); when no
// finite min exists the offers block is omitted entirely.
export function buildDestinationService(dest, allData, siteUrl) {
  if (!dest?.name) return undefined;
  const url = siteUrl + '/dest/' + destinationSlug(dest);
  const destRoutes = (allData?.routes || []).filter(r => r && r.from === 'Athens' && r.to === dest.name);
  const low = routesFromPriceMin(destRoutes);
  // count and highPrice over the UNION of the same per-route sets low is the
  // minimum of — one offer population, three coherent figures.
  const destVals = destRoutes.flatMap(routePriceValues);
  const count = destVals.length;
  const high = destVals.length ? Math.max(...destVals) : null;
  // Same review-snippet rule as the trip node: Product joins the type list so
  // the rating may legally sit here; Service stays for service semantics.
  // Image: the destination's OWN upload (larger of image/heroImage), Pexels
  // seoImages only as last fallback.
  const dimg = jsonLdImageUrl(largerPlaceImage(dest), siteUrl)
    || seoImageFor(allData, 'dest', destinationSlug(dest), siteUrl);
  return clean({
    '@type': ['Product', 'Service'],
    '@id': url + '#service',
    name: dest.name + ' Helicopter Charter',
    description: `Private helicopter charter to ${dest.name}, Greece.`,
    url,
    image: dimg ? [dimg] : undefined,
    brand: { '@id': siteUrl + '/#org' },
    serviceType: 'Helicopter charter',
    category: 'Helicopter Charter',
    provider: { '@id': siteUrl + '/#org' },
    areaServed: { '@type': 'Country', name: 'Greece' },
    review: buildReviewList(dest.reviews),
    aggregateRating: buildAggregateRating(dest.reviews),
    offers: (low != null) ? {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: low,
      ...(high != null ? { highPrice: high } : {}),
      offerCount: count || 1,
      availability: 'https://schema.org/InStock',
      url,
    } : undefined,
  });
}

export function buildTouristDestination(dest, siteUrl) {
  if (!dest?.name) return undefined;
  const slug = destinationSlug(dest);
  const url = siteUrl + '/dest/' + slug;
  const parts = [];
  if (dest.tag) parts.push(dest.tag);
  if (dest.time) parts.push(`${dest.time} from Athens by helicopter`);
  const description = parts.length
    ? parts.join(' \u2014 ')
    : `Helicopter charter destination in Greece.`;
  return clean({
    '@type': 'TouristDestination',
    '@id': url + '#destination',
    name: dest.name,
    description,
    url,
    image: absoluteUrl(dest.heroImage || dest.image, siteUrl),
    touristType: 'Helicopter charter passengers',
    includesAttraction: dest.region ? { '@type': 'Place', name: dest.region } : undefined,
  });
}

// A helicopter charter is a SERVICE, not a fixed-price Product. Pricing is
// route-dependent and there are no reviews yet, so a Product rich result would
// always fail Google's "offers, review, or aggregateRating required" rule
// (GSC: 10 invalid / 0 valid). Emitting @type Service removes that requirement
// entirely and is semantically correct for a charter broker — no fabricated
// price/rating is ever added. A real route price (minAircraftPrice) still adds a
// valid AggregateOffer (Service.offers); when none exists the Service is valid
// with no offers at all. (Kept the export name to avoid touching call sites.)
export function buildProduct(aircraft, allData, siteUrl) {
  if (!aircraft?.name) return undefined;
  const url = aircraft.id ? siteUrl + '/fleet/' + aircraft.id : siteUrl;
  const low = aircraft.id ? minAircraftPrice(aircraft.id, allData?.routes) : null;
  const count = aircraft.id ? countAircraftOffers(aircraft.id, allData?.routes) : 0;
  const manufacturer = aircraft.name.split(' ')[0];
  // Genuine aircraft specs as additionalProperty (informational, no fabrication).
  const props = [
    manufacturer ? { '@type': 'PropertyValue', name: 'Manufacturer', value: manufacturer } : null,
    aircraft.seats != null ? { '@type': 'PropertyValue', name: 'Seats', value: String(aircraft.seats) } : null,
    aircraft.range ? { '@type': 'PropertyValue', name: 'Range', value: aircraft.range } : null,
    aircraft.speed ? { '@type': 'PropertyValue', name: 'Cruise speed', value: aircraft.speed } : null,
    aircraft.engine ? { '@type': 'PropertyValue', name: 'Engine', value: aircraft.engine + '-engine' } : null,
  ].filter(Boolean);
  return clean({
    '@type': 'Service',
    '@id': url + '#service',
    name: aircraft.name + ' Helicopter Charter',
    description: aircraft.longDesc || aircraft.desc,
    url,
    image: absoluteUrl(aircraft.image, siteUrl),
    serviceType: 'Helicopter charter',
    provider: { '@id': siteUrl + '/#org' },
    areaServed: { '@type': 'Country', name: 'Greece' },
    category: 'Helicopter Charter',
    additionalProperty: props.length ? props : undefined,
    aggregateRating: buildAggregateRating(aircraft.reviews),
    // Real route price → a valid AggregateOffer; omitted entirely when no fixed
    // price exists (never an empty/zero placeholder offer).
    offers: (low != null) ? {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: low,
      offerCount: count || 1,
      availability: 'https://schema.org/InStock',
    } : undefined,
  });
}

export function buildLodgingBusiness(hotel, siteUrl) {
  if (!hotel?.name) return undefined;
  const url = hotel.id ? siteUrl + '/hotel/' + hotel.id : siteUrl;
  return clean({
    '@type': 'LodgingBusiness',
    '@id': url + '#hotel',
    name: hotel.name,
    description: hotel.desc,
    url,
    image: absoluteUrl(hotel.image, siteUrl),
    address: {
      '@type': 'PostalAddress',
      addressLocality: hotel.city,
      addressCountry: 'GR',
    },
    geo: (typeof hotel.lat === 'number' && typeof hotel.lng === 'number') ? {
      '@type': 'GeoCoordinates',
      latitude: hotel.lat,
      longitude: hotel.lng,
    } : undefined,
    starRating: (typeof hotel.stars === 'number' && hotel.stars > 0) ? {
      '@type': 'Rating',
      ratingValue: Math.min(5, hotel.stars),
    } : undefined,
    aggregateRating: buildAggregateRating(hotel.reviews),
    amenityFeature: hotel.helipadType ? {
      '@type': 'LocationFeatureSpecification',
      name: 'Helipad access',
      value: hotel.helipadType === 'direct' ? 'Direct helipad on property' : 'Helicopter transfer available',
    } : undefined,
  });
}

export function buildSightseeingTrip(tour, siteUrl) {
  if (!tour?.id || !tour?.name) return undefined;
  const url = siteUrl + '/tour/' + tour.id;
  // Backward-compat: legacy tours store `base` + `duration`/`durations` rather
  // than `departureLocations` + `variants[]`.
  const variants = (tour.variants && tour.variants.length)
    ? tour.variants.filter(v => v && v.duration)
    : (tour.durations && tour.durations.length ? tour.durations : (tour.duration ? [tour.duration] : []))
        .map(d => ({ duration: d }));
  const prices = variants.map(v => v.price).filter(p => typeof p === 'number' && p > 0);
  const lowPrice = prices.length ? Math.min(...prices) : null;
  const offerCount = prices.length;
  const departures = (tour.departureLocations && tour.departureLocations.length)
    ? tour.departureLocations.filter(Boolean)
    : (tour.base ? [tour.base] : []);
  const itinerary = departures.length
    ? { '@type': 'ItemList', itemListElement: departures.map(d => ({ '@type': 'Place', name: d })) }
    : undefined;
  const description = (variants[0] && variants[0].description) || tour.desc
    || `Aerial sightseeing tour in Greece by private helicopter${departures.length ? ' — departing from ' + departures.join(', ') : ''}.`;
  return clean({
    '@type': 'TouristTrip',
    '@id': url + '#trip',
    name: tour.name,
    description,
    url,
    image: absoluteUrl(tour.image, siteUrl),
    touristType: 'Sightseeing passengers',
    itinerary,
    offers: (lowPrice != null) ? {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice,
      offerCount: offerCount || 1,
      availability: 'https://schema.org/InStock',
    } : undefined,
    aggregateRating: buildAggregateRating(tour.reviews),
    provider: { '@id': siteUrl + '/#org' },
  });
}

export function buildHowTo(howItWorks, siteUrl) {
  const steps = (howItWorks?.steps || [])
    .map((s, i) => ({ '@type': 'HowToStep', position: i + 1, name: s?.title, text: s?.desc }))
    .filter(s => s.name && s.text);
  if (!steps.length) return undefined;
  return clean({
    '@type': 'HowTo',
    '@id': siteUrl + '/how-it-works#howto',
    name: 'How to charter a private helicopter in Greece',
    description: howItWorks?.heroSubtitle,
    totalTime: 'PT5M',
    step: steps,
  });
}

export function buildAboutPageNode(s, siteUrl) {
  const text = s?.aboutText;
  return clean({
    '@type': 'AboutPage',
    '@id': siteUrl + '/about#aboutpage',
    url: siteUrl + '/about',
    name: 'About ' + (s?.siteName || BRAND.name),
    description: text ? text.slice(0, 320) : undefined,
    mainEntity: { '@id': siteUrl + '/#org' },
    inLanguage: 'en',
  });
}

export function buildContactPageNode(s, siteUrl) {
  return clean({
    '@type': 'ContactPage',
    '@id': siteUrl + '/contact#contactpage',
    url: siteUrl + '/contact',
    name: 'Contact ' + (s?.siteName || BRAND.name),
    description: 'Reach our charter desk in Athens for private helicopter charters across Greece.',
    mainEntity: { '@id': siteUrl + '/#org' },
    inLanguage: 'en',
  });
}

export function buildService(svc, siteUrl, seo = null) {
  if (!svc?.slug || !svc?.title) return undefined;
  const url = siteUrl + '/' + svc.slug;
  return clean({
    '@type': 'Service',
    '@id': url + '#service',
    name: svc.title,
    description: svc.intro || seo?.intro || svc.subtitle,
    url,
    image: absoluteUrl(svc.hero, siteUrl) || absoluteUrl(seo?.heroImage, siteUrl),
    provider: { '@id': siteUrl + '/#org' },
    areaServed: { '@type': 'Country', name: 'Greece' },
    serviceType: svc.title,
    category: 'Helicopter Charter',
    aggregateRating: buildAggregateRating(svc.reviews),
  });
}

// Strip Markdown into plain text for Schema.org articleBody. Removes
// fenced code blocks, headings, links/images, formatting, and HTML tags;
// collapses whitespace. Clipped to ~5000 chars (Google indexes long bodies
// but JSON-LD stays compact).
function markdownToPlainText(md) {
  if (!md || typeof md !== 'string') return '';
  let txt = md;
  txt = txt.replace(/```[\s\S]*?```/g, ' ');                    // fenced code
  txt = txt.replace(/`[^`]*`/g, ' ');                            // inline code
  txt = txt.replace(/!\[[^\]]*\]\([^)]*\)/g, ' ');               // images
  txt = txt.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');             // links — keep label
  txt = txt.replace(/^#{1,6}\s+/gm, '');                         // ATX headings
  txt = txt.replace(/^[-*+]\s+/gm, '');                          // bullets
  txt = txt.replace(/^\s*\d+\.\s+/gm, '');                       // ordered list
  txt = txt.replace(/^\s*>\s?/gm, '');                           // blockquote
  txt = txt.replace(/[*_~]{1,3}([^*_~]+)[*_~]{1,3}/g, '$1');     // bold/italic/strike
  txt = txt.replace(/<[^>]+>/g, ' ');                            // raw HTML
  txt = txt.replace(/\s+/g, ' ').trim();
  return txt.length > 5000 ? txt.slice(0, 4997) + '...' : txt;
}

export function buildBlogPosting(post, settings, siteUrl) {
  if (!post?.title || !post?.slug) return undefined;
  const url = siteUrl + '/blog/' + post.slug;
  const brand = settings?.siteName || BRAND.name;
  const author = post.author || brand;
  const authorIsOrg = !post.author || post.author === brand;
  const body = markdownToPlainText(post.bodyMarkdown);
  const wordCount = body ? body.split(/\s+/).filter(Boolean).length : undefined;
  return clean({
    '@type': 'BlogPosting',
    '@id': url + '#article',
    headline: post.title,
    description: post.metaDesc || post.excerpt,
    url,
    image: absoluteUrl(post.image, siteUrl),
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      '@type': authorIsOrg ? 'Organization' : 'Person',
      name: author,
    },
    publisher: { '@id': siteUrl + '/#org' },
    keywords: post.tags?.length ? post.tags.join(', ') : undefined,
    articleSection: post.category,
    articleBody: body || undefined,
    wordCount,
    inLanguage: 'en',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  });
}

// -- Composer --

export function buildJsonLd(context) {
  const { path, siteUrl, settings, allData, entity, entityType, faqs, description, seoContent } = context;
  const s = settings || {};
  const isHome = !path || path === '/' || path === '';
  const graph = [];

  const org = buildOrganization(s, siteUrl); if (org) graph.push(org);
  const web = buildWebSite(s, siteUrl); if (web) graph.push(web);
  const biz = buildLocalBusiness(s, siteUrl, description); if (biz) graph.push(biz);

  // FAQPage emit — homepage receives FAQs from settings; detail pages from
  // their seoContent doc. buildFAQPage handles both {q, a} and {question,
  // answer} shapes, so we always pass through context.faqs.
  if (faqs) {
    const faqBlock = buildFAQPage(faqs);
    if (faqBlock) graph.push(faqBlock);
  }
  if (isHome) {
    for (const f of allData?.fleet || []) {
      const p = buildProduct(f, allData, siteUrl);
      if (p) graph.push(p);
    }
  }

  // Category listing pages emit an ItemList of their detail pages plus a
  // Home → Category breadcrumb (they are not "entities" with a Product/Trip
  // node, so they skip the generic entity switch below).
  if (entityType === 'categoryList' && entity) {
    const itemList = buildCategoryItemList(entity.category, allData, siteUrl);
    if (itemList) graph.push(itemList);
    const label = { fleet: 'Fleet', hotels: 'Hotels', destinations: 'Destinations', routes: 'Routes', tours: 'Sightseeing Tours' }[entity.category] || 'Browse';
    const cb = buildBreadcrumb([{ name: 'Home', url: '/' }, { name: label, url: path }], siteUrl);
    if (cb) graph.push(cb);
  }

  // Pages with no CMS entity of their own — the journal listing, the HTML
  // sitemap and the three legal pages. They are ordinary indexable pages that
  // simply fall outside resolveEntity, so they were the only indexable URLs on
  // the site emitting no BreadcrumbList at all. One flat Home → Page trail,
  // same shape the category pages use.
  const FLAT_CRUMB_PAGES = {
    '/blog': 'Journal',
    '/sitemap': 'Sitemap',
    '/terms': 'Terms and Conditions',
    '/privacy': 'Privacy Policy',
    '/cookies': 'Cookie Policy',
  };
  const flatPath = typeof path === 'string' ? path.replace(/\/+$/, '') || '/' : '/';
  if (!entity && FLAT_CRUMB_PAGES[flatPath]) {
    const cb = buildBreadcrumb(
      [{ name: 'Home', url: '/' }, { name: FLAT_CRUMB_PAGES[flatPath], url: flatPath }],
      siteUrl,
    );
    if (cb) graph.push(cb);
  }

  if (entity && entityType && entityType !== 'categoryList') {
    const crumbs = [{ name: 'Home', url: '/' }];
    if (entityType === 'route') {
      // Intermediate /routes level mirrors the visible breadcrumb UI
      // (Home > Routes > From to To) and gives the crumb trail a real
      // category landing page.
      crumbs.push({ name: 'Routes', url: '/routes' });
      crumbs.push({ name: `${entity.from} \u2192 ${entity.to}`, url: path });
      // Genuine published CMS reviews activate the prepared rating slot.
      const routeReviews = publishedReviewsFor('route', entity, allData?.reviews);
      const trip = buildTouristTrip(routeReviews.length ? { ...entity, reviews: routeReviews } : entity, allData, siteUrl);
      if (trip) graph.push(trip);
      // Prepared fixed-price Product path (distribution) \u2014 renders only when
      // a curated, active product doc with a finite price targets this route.
      for (const pr of (allData?.products || [])) {
        if (pr?.route?.slug === entity.slug) {
          const node = buildFixedPriceProduct(pr, siteUrl);
          if (node) graph.push(node);
        }
      }
    } else if (entityType === 'destination') {
      crumbs.push({ name: 'Destinations', url: '/destinations' });
      crumbs.push({ name: entity.name, url: path });
      const d = buildTouristDestination(entity, siteUrl);
      if (d) graph.push(d);
      // Service + provider + finite-guarded offers for the dest charter
      // (TouristDestination itself cannot carry offers).
      const destReviews = publishedReviewsFor('destination', entity, allData?.reviews);
      const ds = buildDestinationService(destReviews.length ? { ...entity, reviews: destReviews } : entity, allData, siteUrl);
      if (ds) graph.push(ds);
    } else if (entityType === 'aircraft') {
      crumbs.push({ name: 'Fleet', url: '/fleet' });
      crumbs.push({ name: entity.name, url: path });
      const p = buildProduct(entity, allData, siteUrl);
      if (p) graph.push(p);
    } else if (entityType === 'hotel') {
      crumbs.push({ name: 'Hotels', url: '/hotels' });
      crumbs.push({ name: entity.name, url: path });
      const h = buildLodgingBusiness(entity, siteUrl);
      if (h) graph.push(h);
    } else if (entityType === 'blogPost') {
      crumbs.push({ name: 'Journal', url: '/blog' });
      crumbs.push({ name: entity.title, url: path });
      const b = buildBlogPosting(entity, s, siteUrl);
      if (b) graph.push(b);
    } else if (entityType === 'howItWorks') {
      crumbs.push({ name: 'How it works', url: path });
      const ht = buildHowTo(entity, siteUrl);
      if (ht) graph.push(ht);
      const fp = buildFAQPage(entity?.faqs);
      if (fp) graph.push(fp);
    } else if (entityType === 'about') {
      crumbs.push({ name: 'About', url: path });
      const ap = buildAboutPageNode(s, siteUrl);
      if (ap) graph.push(ap);
    } else if (entityType === 'safety') {
      crumbs.push({ name: 'Safety', url: path });
    } else if (entityType === 'contact') {
      crumbs.push({ name: 'Contact', url: path });
      const cp = buildContactPageNode(s, siteUrl);
      if (cp) graph.push(cp);
    } else if (entityType === 'charterService') {
      crumbs.push({ name: 'Charter services' });
      crumbs.push({ name: entity.title, url: path });
      const sv = buildService(entity, siteUrl, seoContent);
      if (sv) graph.push(sv);
    } else if (entityType === 'sightseeingTour') {
      crumbs.push({ name: 'Sightseeing tours', url: '/charter-sightseeing' });
      crumbs.push({ name: entity.name, url: path });
      const t = buildSightseeingTrip(entity, siteUrl);
      if (t) graph.push(t);
    }
    const cb = buildBreadcrumb(crumbs, siteUrl);
    if (cb) graph.push(cb);
  }

  return {
    '@context': SCHEMA,
    '@graph': graph.filter(Boolean),
  };
}

// -- URL-to-entity resolver --

export function resolveEntity(path, allData) {
  if (!path) return { entity: null, entityType: null };
  const segs = path.split('/').filter(Boolean);

  // Single-segment static pages (How It Works, About, Safety, Contact, Charter-*).
  // These are not "entities" in the routing sense, but we synthesize a typed
  // payload from Sanity (or siteSettings) so [...slug].astro can render
  // page-specific SSR HTML + meta tags + JSON-LD instead of falling back to
  // the homepage default.
  if (segs.length === 1) {
    const only = segs[0];
    if (only === 'how-it-works') {
      const e = allData?.howItWorks || null;
      return e ? { entity: e, entityType: 'howItWorks' } : { entity: null, entityType: null };
    }
    if (only === 'about') {
      const cms = (allData?.pages || []).find(p => p?.slug === 'about') || {};
      return { entity: cms, entityType: 'about' };
    }
    if (only === 'safety') {
      const cms = (allData?.pages || []).find(p => p?.slug === 'safety') || {};
      return { entity: cms, entityType: 'safety' };
    }
    if (only === 'contact') {
      const cms = (allData?.pages || []).find(p => p?.slug === 'contact') || {};
      return { entity: cms, entityType: 'contact' };
    }
    if (only.startsWith('charter-')) {
      const e = (allData?.charterServices || []).find(cs => cs?.slug === only);
      if (e) return { entity: e, entityType: 'charterService' };
      // 2026-05-19j — fall back to hardcoded CHARTER_SERVICES_FALLBACK if
      // the Sanity dataset is missing the doc. Defense-in-depth so /charter-*
      // pages never render with a blank middle (observed 2026-05-19 on
      // /charter-custom when the cs-custom doc had not yet been seeded).
      const fb = CHARTER_SERVICES_FALLBACK[only];
      if (fb) return { entity: { slug: only, ...fb }, entityType: 'charterService' };
      return { entity: null, entityType: null };
    }
    // Category listing pages (/fleet, /hotels, /destinations, /routes, /tours).
    // These render a dedicated SSR grid of crawlable <a href> links to every
    // detail page of the type, so /fleet/* and /tour/* are no longer an
    // orphaned crawl island (they previously fell through to the generic
    // homepage block, which linked Dest/Hotel/Route but NOT Fleet/Tours).
    // entity carries the category key; entityType is 'categoryList'.
    const CATEGORY_PAGES = ['fleet', 'hotels', 'destinations', 'routes', 'tours'];
    if (CATEGORY_PAGES.includes(only)) {
      return { entity: { category: only }, entityType: 'categoryList' };
    }
  }

  if (segs.length < 2) return { entity: null, entityType: null };
  const [prefix, rawSlug] = segs;
  if (!rawSlug) return { entity: null, entityType: null };
  let slug;
  try { slug = decodeURIComponent(rawSlug); } catch { slug = rawSlug; }
  switch (prefix) {
    case 'route': {
      const e = (allData?.routes || []).find(r => r.slug === slug);
      return e ? { entity: e, entityType: 'route' } : { entity: null, entityType: null };
    }
    case 'dest':
    case 'destinations': {
      const e = (allData?.destinations || []).find(d => destinationSlug(d) === slug);
      return e ? { entity: e, entityType: 'destination' } : { entity: null, entityType: null };
    }
    case 'fleet': {
      const e = (allData?.fleet || []).find(f => f.id === slug);
      return e ? { entity: e, entityType: 'aircraft' } : { entity: null, entityType: null };
    }
    case 'hotel':
    case 'hotels': {
      const e = (allData?.hotels || []).find(h => h.id === slug);
      return e ? { entity: e, entityType: 'hotel' } : { entity: null, entityType: null };
    }
    case 'blog': {
      const e = (allData?.blogs || []).find(b => b.slug === slug);
      return e ? { entity: e, entityType: 'blogPost' } : { entity: null, entityType: null };
    }
    case 'tour': {
      // Tours don't have an explicit slug field — match on `id` (already
      // URL-friendly, e.g. "santorini-caldera") with a fallback to a
      // normalised name so legacy tours resolve too.
      const tours = allData?.tours || [];
      const e = tours.find(t => t.id === slug)
        || tours.find(t => t?.name && t.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') === slug);
      return e ? { entity: e, entityType: 'sightseeingTour' } : { entity: null, entityType: null };
    }
    default:
      return { entity: null, entityType: null };
  }
}
